//dashboard/src/pages/programManagerDashboard/PDAssetsPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { 
  Box, Shield, CornerUpLeft, Loader2, 
  Package, CheckCircle, Clock, Archive,
  LayoutDashboard, Search, Filter as FilterIcon,
  ChevronRight, Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import SidebarNav from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav"; 
import MyAssetsTable from "../../components/operationsAssociate/MyAssetsTable"; 
import ReturnAssetModal from "../../components/operationsAssociate/ReturnAssetModal"; 
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal"; 
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { Navigate, useNavigate } from "react-router-dom";

const API_URL = "https://ims.holdthechild.net";

export const ProtectedRoute = ({ children }) => {
    const token = localStorage.getItem("token");
    return token ? children : <Navigate to="/" />;
};

const PDAssetsPage = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [loggedInStaffId, setLoggedInStaffId] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                // Handle both 'id' and '_id' formats for robustness
                if (user && user.id) {
                    setLoggedInStaffId(user.id);
                } else if (user && user._id) {
                    setLoggedInStaffId(user._id);
                } else {
                    toast.error("User ID not found in session.");
                    navigate("/");
                }
            } catch (e) {
                console.error("Error parsing user from localStorage", e);
            }
        } else {
            toast.error("User not logged in or session expired.");
            navigate("/");
        }
    }, [navigate]);

    const fetchMyAssets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");
            
            const response = await axios.get(`${API_URL}/api/assets/my-assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            console.error("Failed to fetch my assets:", err);
            setError("Unable to fetch your assets.");
            toast.error("Server error while loading assets.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (loggedInStaffId) {
            fetchMyAssets();
        }
    }, [loggedInStaffId, fetchMyAssets]);

    const handleOpenReturnModal = (asset) => {
        setSelectedAsset(asset);
        setIsReturnModalOpen(true);
    };

    const handleOpenViewModal = (asset) => {
        setSelectedAsset(asset);
        setIsViewModalOpen(true);
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
                    <div className="max-w-7xl mx-auto space-y-8">
                        
                        <div className="flex flex-col items-center text-center mb-12">
                            <PremiumHeader 
                                title="Executive" 
                                lastWordAccent="Assets" 
                                subtitle="Professional inventory management for high-value equipment and technology assigned to the directorate." 
                            />
                        </div>

                        {/* Quick Stats Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                                <PremiumStatCard 
                                    title="Active Inventory" 
                                    value={assets.length} 
                                    icon={<Box className="text-orange-600" />} 
                                    iconBg="bg-orange-50"
                                    description="Assigned equipment"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                                {/* Note: 'Assigned' might need to be 'Active' depending on your status enum, kept as is based on original code */}
                                <PremiumStatCard 
                                    title="Verified Assets" 
                                    value={assets.filter(a => a.status === 'Active').length} 
                                    icon={<Shield className="text-blue-600" />} 
                                    iconBg="bg-blue-50"
                                    description="Confirmed custody"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                                <PremiumStatCard 
                                    title="Operational Ready" 
                                    value={assets.length} 
                                    icon={<CheckCircle className="text-emerald-600" />} 
                                    iconBg="bg-emerald-50"
                                    description="Active status items"
                                />
                            </motion.div>
                        </div>

                        {/* Main Asset Table */}
                        <PremiumCard icon={<Archive size={18} />} title="Directors Custody Registry">
                            <div className="min-h-[400px]">
                                {loading ? (
                                    <div className="py-24 flex flex-col items-center justify-center">
                                        <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Accessing Directory Assets...</p>
                                    </div>
                                ) : (
                                    <div className="p-2">
                                        {/* 
                                            UPDATED: Added refreshAssets={fetchMyAssets} 
                                            This ensures that when you Accept/Reject inside the table, 
                                            the list refreshes immediately to show the new status.
                                        */}
                                        <MyAssetsTable
                                            assets={assets}
                                            error={error}
                                            onReturn={handleOpenReturnModal}
                                            onView={handleOpenViewModal}
                                            loggedInStaffId={loggedInStaffId}
                                            refreshAssets={fetchMyAssets}
                                        />
                                    </div>
                                )}
                            </div>
                        </PremiumCard>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {isReturnModalOpen && selectedAsset && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsReturnModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
                            <ReturnAssetModal
                                asset={selectedAsset}
                                closeModal={() => {
                                    setIsReturnModalOpen(false);
                                    fetchMyAssets();
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isViewModalOpen && selectedAsset && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsViewModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100">
                            <ViewAssetModal
                                asset={selectedAsset}
                                closeModal={() => setIsViewModalOpen(false)}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PDAssetsPage;